/* Tomorrow Co's page: the store's dark body with Tomorrow Co's palette as accents
   (owner, 2026-11-04). --tc-hero-angle / --tc-hero-glow / --tc-section-gap / --tc-grid-gap are
   driven by the ?tune=1 tuner. */
.page {
  min-height: 100vh;
  padding: calc(var(--nav-height) + clamp(28px, 6vh, 64px)) 28px clamp(64px, 10vh, 120px);
  background:
    radial-gradient(1200px 800px at 80% -10%, rgba(39, 75, 58, calc(var(--tc-hero-glow, 18) / 100)), transparent 60%),
    var(--st-bg);
  color: #f8f8fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--tc-section-gap, clamp(40px, 6vh, 72px));
}

/* ──────────── HERO ──────────── */
.hero {
  width: 100%;
  max-width: 1240px;
  padding: clamp(40px, 7vw, 88px) clamp(24px, 5vw, 64px);
  border-radius: 24px;
  background: linear-gradient(var(--tc-hero-angle, 135deg), var(--tc-forest) 0%, var(--tc-charcoal) 100%);
  color: var(--tc-cream);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.eyebrow {
  font-family: var(--font-dm-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--tc-gold);
}

.title {
  margin: 0;
  font-family: var(--font-syne);
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--tc-cream);
}

.rule {
  width: 56px;
  height: 3px;
  border-radius: var(--radius-pill, 100px);
  background: var(--tc-gold);
}

.sub {
  margin: 0;
  max-width: 60ch;
  font-family: var(--font-dm-sans);
  font-size: clamp(1rem, 1.3vw, 1.125rem);
  line-height: 1.6;
  color: var(--tc-sand);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
}

.btnPrimary,
.btnSecondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 80px;
  padding: 0 20px;
  border-radius: var(--radius-pill, 100px);
  font-family: var(--font-dm-sans);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  text-decoration: none;
  transition:
    background-color 150ms ease,
    color 150ms ease,
    border-color 150ms ease,
    transform 150ms ease;
}

.btnPrimary {
  background: var(--tc-gold);
  border: 1px solid var(--tc-gold);
  color: var(--tc-charcoal);
}

.btnPrimary:hover {
  background: #ebb15a;
  border-color: #ebb15a;
}

.btnSecondary {
  background: transparent;
  border: 1px solid var(--tc-cream);
  color: var(--tc-cream);
}

.btnSecondary:hover {
  background: rgba(247, 242, 230, 0.12);
}

.btnPrimary:active,
.btnSecondary:active {
  transform: scale(0.97);
}

.btnPrimary:focus-visible,
.btnSecondary:focus-visible {
  outline: 2px solid var(--tc-gold);
  outline-offset: 2px;
}

@media (hover: none) {
  .btnPrimary,
  .btnSecondary {
    min-height: 44px;
  }
}

.disclaimer {
  margin: 0;
  max-width: 60ch;
  font-family: var(--font-dm-sans);
  font-size: 12px;
  color: rgba(247, 242, 230, 0.7);
}

/* ──────────── SECTIONS ──────────── */
.section {
  width: 100%;
  max-width: 1240px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  scroll-margin-top: calc(var(--nav-height) + 16px);
}

.h2 {
  margin: 0;
  font-family: var(--font-syne);
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  font-weight: 700;
  color: #f8f8fa;
}

.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--tc-grid-gap, 16px);
}

.empty {
  margin: 0;
  padding: 48px 24px;
  text-align: center;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  font-family: var(--font-dm-sans);
  font-size: 1.05rem;
  color: var(--st-muted);
}

/* ──────────── MISSION ──────────── */
.missionText {
  margin: 0;
  max-width: 65ch;
  font-family: var(--font-dm-sans);
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
}

.callout {
  align-self: start;
  padding: 24px;
  border-left: 3px solid var(--tc-gold);
  border-radius: 20px;
  background: var(--tc-forest);
  color: var(--tc-cream);
  font-family: var(--font-syne);
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
}

@media (min-width: 768px) {
  .mission {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    column-gap: 32px;
    row-gap: 24px;
  }

  .mission .h2 {
    grid-column: 1 / -1;
  }
}

/* ──────────── PHONES ──────────── */
@media (max-width: 760px) {
  .page {
    padding-left: 18px;
    padding-right: 18px;
  }

  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--tc-grid-gap, 12px);
  }
}
